import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { sdk } from "./sdk"

export type CustomerRow = {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  phone: string | null
  has_account: boolean
  created_at: string
  email_marketing: string | null
  sms_marketing: string | null
  previous_store: PreviousStore | null
  /** previous_store = brought over from the old store (recovery); online_store = ordered/signed up here; added = staff or import. */
  source: "previous_store" | "online_store" | "added"
  tags: string[]
  location: string | null
  orders_count: number
  amount_spent: number
  last_order_at: string | null
}

/** The customer's history in the previous store (old Shopify store), metadata.previous_store. */
export type PreviousStore = {
  source: "shopify"
  id: string
  since: string | null
  orders: number
  spent: number
  currency: string
  last_order_name: string | null
  last_order_at: string | null
  email_marketing: string
  sms_marketing: string
  tags: string[]
  synced_at: string
}

export type CustomerListParams = {
  q?: string
  segment?: string
  sort?: string
  email_marketing?: string
  sms_marketing?: string
  previous_store?: string
  source?: string
  limit: number
  offset: number
}

export function useCustomerList(p: CustomerListParams) {
  return useQuery({
    queryKey: ["customers", "list", p],
    placeholderData: keepPreviousData,
    queryFn: () =>
      sdk.client.fetch<{ customers: CustomerRow[]; count: number }>("/admin/terify/customers", {
        query: {
          q: p.q?.trim() || undefined,
          segment: p.segment || undefined,
          sort: p.sort && p.sort !== "created_desc" ? p.sort : undefined,
          email_marketing: p.email_marketing || undefined,
          sms_marketing: p.sms_marketing || undefined,
          previous_store: p.previous_store || undefined,
          source: p.source || undefined,
          limit: p.limit,
          offset: p.offset,
        },
      }),
  })
}

export function useBulkCustomers() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { ids: string[]; action: "add_tags" | "remove_tags"; tags: string[] }) => sdk.client.fetch<{ updated: number }>("/admin/terify/customers/bulk", { method: "POST", body: v }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["customers"] }),
  })
}

export function useCustomerDetail(id: string) {
  return useQuery({ queryKey: ["customer", id], queryFn: () => sdk.admin.customer.retrieve(id, { fields: "*addresses,+metadata,+has_account" } as any) })
}

export function useCustomerSummary(id: string) {
  return useQuery({
    queryKey: ["customer", id, "summary"],
    queryFn: () =>
      sdk.client.fetch<{
        orders_count: number
        amount_spent: number
        average_order_value: number
        last_order_at: string | null
        recent_orders: { id: string; display_id: number; created_at: string; status: string; total: number; kind?: "one_time" | "subscription" | "renewal"; channel?: "online_store" | "draft_order" | "subscriptions"; currency_code: string; payment_status: string; fulfillment_status: string; items: { id: string; title: string; quantity: number; thumbnail: string | null }[] }[]
      }>(`/admin/terify/customers/${id}/summary`),
  })
}

function useCustomerMutation<T>(id: string | null, fn: (v: T) => Promise<any>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] })
      if (id) qc.invalidateQueries({ queryKey: ["customer", id] })
    },
  })
}

export type CustomerInput = { first_name?: string | null; last_name?: string | null; email?: string; phone?: string | null; company_name?: string | null; metadata?: Record<string, unknown> }

export function useCreateCustomer() {
  return useCustomerMutation(null, (v: CustomerInput & { address?: AddressInput | null }) =>
    sdk.admin.customer.create({ ...v, address: undefined } as any).then(async ({ customer }) => {
      if (v.address) await sdk.admin.customer.createAddress(customer.id, { ...v.address, is_default_shipping: true, is_default_billing: true } as any)
      return customer
    })
  )
}

export function useUpdateCustomer(id: string) {
  const qc = useQueryClient()
  return useCustomerMutation(id, (v: CustomerInput) =>
    sdk.admin.customer.update(id, v as any).then((r) => {
      // A new email moves their subscriptions to it (backend): show the new address there too.
      if (v.email !== undefined) {
        qc.invalidateQueries({ queryKey: ["subscriptions"] })
        qc.invalidateQueries({ queryKey: ["recovery"] })
      }
      return r
    })
  )
}

export function useDeleteCustomer(id: string) {
  return useCustomerMutation(id, () => sdk.admin.customer.delete(id))
}

export type AddressInput = {
  first_name?: string
  last_name?: string
  company?: string
  address_1?: string
  address_2?: string
  city?: string
  province?: string
  postal_code?: string
  country_code?: string
  phone?: string
  is_default_shipping?: boolean
  is_default_billing?: boolean
}

export function useSaveAddress(customerId: string) {
  return useCustomerMutation(customerId, (v: { id?: string; address: AddressInput }) =>
    v.id ? sdk.admin.customer.updateAddress(customerId, v.id, v.address as any) : sdk.admin.customer.createAddress(customerId, v.address as any)
  )
}

export function useDeleteAddress(customerId: string) {
  return useCustomerMutation(customerId, (addressId: string) => sdk.admin.customer.deleteAddress(customerId, addressId))
}
